/* eslint-disable max-lines-per-function, max-lines, complexity, camelcase, capitalized-comments, max-statements, no-console, prefer-named-capture-group */
/* eslint-disable react/no-unescaped-entities, react/jsx-no-bind, react/jsx-no-leaked-render */
import * as yup from "yup";
import { Alert, Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { stateCodesCA, stateCodesUS } from "./stateCodes";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import PropTypes from "prop-types";
import { useNavigate } from "react-router";

import { Formik } from "formik";
import purchaseSingleCredit from "./purchaseSingleCreditMutation";
import { useAuth } from "../services/eqqo.auth.service";
import { useUser } from "../UserContext";

const initialValues = {
	billingCity: "",
	billingCountry: "US",
	billingState: "",
	billingStreet: "",
	billingZip: "",
	name: ""
};

const validationSchema = yup.object({
	billingCity: yup.string().required("City is required."),
	billingCountry: yup.string().required("Country is required."),
	billingState: yup.string().
		max(2).
		required("State/Province is required"),
	billingStreet: yup.string().required("Street address is required"),
	billingZip: yup
		.string()
		.when(
			"billingCountry",
			([billingCountry]) => {
				if (billingCountry === "CA") {
					return yup.string().length(7, 'Must be exactly 7 characters').required("Postal Code is required");
				}
				return yup.string().matches(/^[0-9]{5}$/u, 'Must be exactly 5 digits').required("Zip Code is required");
			}
		),
	name: yup.string().required("Name is required"),
	// tou validation must match ['on']
	tou: yup
		.array()
		.test("is-true", "You must agree to the Terms of Use", (value) => value.length > 0)
		.required("You must agree to the Terms of Use")
});

const PaymentForm = ({ promoCode }) => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { user } = useAuth();
	const { eqqoOptions } = useUser();
	const { mutateAsync: purchaseCredit } = purchaseSingleCredit(useMutation, user.databaseId);

	const { data: touText } = useQuery(
		["touText"],
		() => fetch( `${process.env.REACT_APP_API_URL}/wp-json/wp/v2/pages/${eqqoOptions?.tou_page}`).then( res => res.json() ),
		{ 
			enabled: !!eqqoOptions?.tou_page,
			staleTime: 600000
		}
	);
	const [touOpen, setTouOpen] = useState(false);
	const toggleTou = useCallback(() => {
		setTouOpen((prev) => !prev);
	}, []);

	const nameField = useRef();
	useEffect(() => {
		nameField.current.focus();
	}, []);

	const [loading, setLoading] = useState(false);
	const [country, setCountry] = useState("US");
	const stateCodes = useMemo(() => {
		if (country === "US") { return stateCodesUS; }
		return stateCodesCA
	}, [country]);

	const [errorMessage, setErrorMessage] = useState(null);

	const stripe = useStripe();
	const elements = useElements();

	const handleFormSubmit = useCallback(async (values) => {
		setErrorMessage(null);
		if (!stripe || !elements) {
			return null;
		}
		setLoading(true);

		try {
			/* eslint-disable sort-keys */
			const token = await stripe.createToken(
				elements.getElement(CardElement),
				{
					name: values.name,
					address_line1: values.billingStreet,
					address_city: values.billingCity,
					address_state: values.billingState,
					address_zip: values.billingZip,
					address_country: values.billingCountry
				}
			);
			/* eslint-enable sort-keys */
			if (token.error) {
				throw new Error(token.error.message);
			}
			const address = {
				city: values.billingCity,
				country: values.billingCountry,
				line1: values.billingStreet,
				postal_code: values.billingZip,
				state: values.billingState
			};
			const newSingleCreditPurchase = await purchaseCredit({
				address,
				paymentToken: token.token.id,
				promoCode: promoCode?.code || null
			});
			console.log(newSingleCreditPurchase);

			await queryClient.invalidateQueries(["respondent", user.respondentId]);
			// Confirm if successful, then...
			navigate("/sign-up/pay/success");
		} catch (error) {
			console.log(error);
			setErrorMessage(error.message);
		}

		setLoading(false);

		return null;
	}, [stripe, elements, purchaseCredit, queryClient, navigate, user, promoCode]);

	return (
		<>
			<Formik
				initialValues={initialValues}
				onSubmit={handleFormSubmit}
				validationSchema={validationSchema}
			>
				{(formik) => (
					<Form className="flow" noValidate onSubmit={formik.handleSubmit}>
						<h2 className="mb-4">Card Information</h2>
						<Row>
							<Col>
								<Form.Label htmlFor="name">Name on Card</Form.Label>
								<Form.Control
									autocomplete="cc-name"
									isInvalid={Boolean(formik.touched.name && formik.errors.name)}
									name={"name"}
									onBlur={formik.handleBlur}
									onChange={formik.handleChange}
									ref={nameField}
									type="text"
								/>
								<Form.Control.Feedback name={"name"} type="invalid">
									{formik.touched.name && formik.errors.name ? <p>{formik.errors.name}</p> : null}
								</Form.Control.Feedback>
							</Col>
						</Row>
						<Row>
							<Col>
								<CardElement className="stripe-card-input" />
							</Col>
						</Row>
						<hr />
						<h2 className="mb-4">Billing Address</h2>
						<Row>
							<Col>
								<Form.Label htmlFor="billingCountry">Country</Form.Label>
								<Form.Select
									autocomplete="country"
									isInvalid={Boolean(formik.touched.billingCountry && formik.errors.billingCountry)}
									name={"billingCountry"}
									onBlur={formik.handleBlur}
									onChange={(evt) => {
										formik.handleChange("billingCountry")(evt);
										setCountry(evt.currentTarget.value);
									}}
									value={formik.values.billingCountry}
								>
									<option value="US">United States</option>
									<option value="CA">Canada</option>
								</Form.Select>
								<Form.Control.Feedback name={"billingCountry"} type="invalid">
									{formik.touched.billingCountry && formik.errors.billingCountry ? <p>{formik.errors.billingCountry}</p> : null}
								</Form.Control.Feedback>
							</Col>
						</Row>
						<Row>
							<Col>
								<Form.Label htmlFor="billingStreet">Street Address</Form.Label>
								<Form.Control
									autocomplete="billing street-address"
									isInvalid={Boolean(formik.touched.billingStreet && formik.errors.billingStreet)}
									name={"billingStreet"}
									onBlur={formik.handleBlur}
									onChange={formik.handleChange}
									type="text"
								/>
								<Form.Control.Feedback name={"billingStreet"} type="invalid">
									{formik.touched.billingStreet && formik.errors.billingStreet ? <p>{formik.errors.billingStreet}</p> : null}
								</Form.Control.Feedback>
							</Col>
						</Row>
						<Row>
							<Col>
								<Form.Label htmlFor="billingCity">City</Form.Label>
								<Form.Control
									autocomplete="billing address-level2"
									isInvalid={Boolean(formik.touched.billingCity && formik.errors.billingCity)}
									name={"billingCity"}
									onBlur={formik.handleBlur}
									onChange={formik.handleChange}
									type="text"
								/>
								<Form.Control.Feedback name={"billingCity"} type="invalid">
									{formik.touched.billingCity && formik.errors.billingCity ? <p>{formik.errors.billingCity}</p> : null}
								</Form.Control.Feedback>
							</Col>
							<Col>
								<Form.Label htmlFor="billingState">{formik.values.billingCountry === "CA" ? "Province" : "State"}</Form.Label>
								<Form.Select
									autocomplete="billing address-level1"
									isInvalid={Boolean(formik.touched.billingState && formik.errors.billingState)}
									name={"billingState"}
									onBlur={formik.handleBlur}
									onChange={formik.handleChange}
									type="text"
								>
									<option>Choose One</option>
									{stateCodes.map((state) => 
										<option key={state.abbreviation} value={state.abbreviation}>{state.abbreviation}</option>
									)}
								</Form.Select>
								<Form.Control.Feedback name={"billingState"} type="invalid">
									{formik.touched.billingState && formik.errors.billingState ? <p>{formik.errors.billingState}</p> : null}
								</Form.Control.Feedback>
							</Col>
							<Col>
								<Form.Label htmlFor="billingZip">{formik.values.billingCountry === "CA" ? "Postal Code" : "Zip Code"}</Form.Label>
								<Form.Control
									autocomplete="billing postal-code"
									isInvalid={Boolean(formik.touched.billingZip && formik.errors.billingZip)}
									name={"billingZip"}
									onBlur={formik.handleBlur}
									onChange={formik.handleChange}
									type="text"
								/>
								<Form.Control.Feedback name={"billingZip"} type="invalid">
									{formik.touched.billingZip && formik.errors.billingZip ? <p>{formik.errors.billingZip}</p> : null}
								</Form.Control.Feedback>
							</Col>
						</Row>
						<Form.Check id="tou" type="checkbox">
							<Form.Check.Input
								isInvalid={Boolean(formik.errors.tou)}
								isValid={Boolean(!formik.errors.tou)}
								name="tou"
								onBlur={formik.handleBlur}
								onChange={formik.handleChange}
								type="checkbox"
							/>
							<Form.Check.Label />
							<p className="d-inline-flex ms-1 align-items-center gap-1">
								I have read and agree to the <Button className="p-0 d-inline" onClick={toggleTou} variant="link">Terms of Use</Button>
							</p>
							{formik.errors.tou
								? (
									<Form.Control.Feedback name={"tou"} type="invalid">
										<p>{formik.errors.tou}</p>
									</Form.Control.Feedback>
								) : null}
						</Form.Check>
						{
							errorMessage
								? (
									<Alert className="my-1 p-2" variant="danger">
										<ExclamationCircleIcon className="i" style={{ height: "3em" }} />
										<span className="ms-2">{errorMessage}</span>
									</Alert>
								) : null
						}
						<Button className="btn-gradient w-100" disabled={loading} type="submit" variant="primary">{
							loading
								? <Spinner animation="border" />
								: "Make Payment"
						}
						</Button>
					</Form>
				)}
			</Formik>
			<Modal onHide={toggleTou} show={touOpen} size="lg">
				<Modal.Header closeButton>
					<Modal.Title>Terms of Use</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{touText ? <div dangerouslySetInnerHTML={{ __html: touText?.content?.rendered }} /> : <Spinner animation="border" />}
				</Modal.Body>
				<Modal.Footer>
					<Button onClick={toggleTou} variant="secondary">
						Close
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	);
};

PaymentForm.propTypes = {
	promoCode: PropTypes.string
};

PaymentForm.defaultProps = {
	promoCode: null
};

export default PaymentForm;
